/* eslint-disable no-plusplus */
/* eslint-disable no-unsafe-optional-chaining */
import {
  Card,
  Divider,
  GKDataGrid,
  Grid2,
  IconButton,
  ReloadIcon,
  Tab,
  Tabs,
  Typography,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { useFetchQuery } from "hooks/useQueries";
import _ from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { GET_HOLDING } from "utils/apiRoutes";
import { assetsHoldingsColumns } from "utils/columns";
import {
  LARGE_CAP_COLOR,
  MID_CAP_COLOR,
  PieChartColors,
  SMALL_CAP_COLOR,
} from "utils/constants";
import PieChartSection from "./PieChartSection";
import SunburstSection from "./SunburstSection";

interface InsigntsPageContentProps {
  allHoldingsInsightsData?: any[];
  isLoading: boolean;
  onRowClick?: (row: any) => void;
  selectedItems: any[];
}

const InsigntsPageContent = (props: InsigntsPageContentProps) => {
  const {
    allHoldingsInsightsData,
    isLoading = false,
    onRowClick,
    selectedItems,
  } = props;

  const [pieClickData, setPieClickData] = useState<any>();

  const [currentValueTabs, setCurrentValueTabs] = useState(0);

  const [sunBurstClickData, setSunBurstClickData] = useState<any>();

  const [sunBurstHoverData, setSunBurstHoverData] = useState<any>();

  const [pieHoverData, setPieHoverData] = useState<any>();

  const sunBurstRef = useRef(null);

  const pieRef = useRef(null);

  const [pieData, setPieData] = useState<any>([]);

  const [sunBurstData, setSunBurstData] = useState<any>([]);

  const [capSizes, setCapSizes] = useState<any>([]);

  const requestBody = useGenerateQueryParams("insights", {
    portfolios: JSON.stringify(
      selectedItems
        ?.map((data: any) => data?.portfolios)
        ?.flat()
        ?.map((dataItem) => dataItem?.value),
    ),
  });

  const { data: holdingData, isFetching: holdingLoading } = useFetchQuery({
    key: [
      "INSIGHTS",
      requestBody,
      selectedItems
        ?.map((data: any) => data?.portfolios)
        ?.flat()
        ?.map((dataItem) => dataItem?.value),
    ],
    retry: 0,
    route: GET_HOLDING,
    requestBody,
  });

  const checkCurrentValue = (sum: any, current: any) =>
    currentValueTabs === 0
      ? sum + current?.currentAmount
      : sum + current?.investedAmount;

  const totalInvestment = allHoldingsInsightsData?.reduce(
    (sum: any, current: any) => checkCurrentValue(sum, current),
    0,
  );

  const sectorsArray = () =>
    allHoldingsInsightsData.map((item: any) => ({
      sector: item.asset.sector,
      companyName: item.asset.name,
      investedAmount: item.investedAmount,
      companyPercentage:
        currentValueTabs === 0
          ? (item.currentAmount / totalInvestment) * 100
          : (item.investedAmount / totalInvestment) * 100,
    }));

  const cumulativeSectorsWithPercentage = () =>
    sectorsArray()?.reduce((acc: any, current: any) => {
      const x = acc.find((item: any) => item.sector === current.sector);
      if (!x) {
        return acc.concat([current]);
      }
      return acc?.map((item: any) =>
        item.sector === current.sector
          ? {
              ...item,
              companyPercentage:
                item.companyPercentage + current.companyPercentage,
            }
          : item,
      );
    }, []);

  // ========================================================================================================================================================================

  const handleNewFn = (capName: string, amountType: any) => {
    const groupedData = _.groupBy(
      allHoldingsInsightsData?.map((data) => {
        return {
          ...data,
          cap: data.asset?.cap,
        };
      }),
      "cap",
    );

    const allCapSum = Object.entries(groupedData)?.map(([key, value]) => {
      return {
        children: value,
        cap: key,
        totalCapValue: _.sum(
          value?.map((item) =>
            amountType === 0 ? item?.currentAmount : item?.investedAmount,
          ),
        ),
        totalCapPercentage:
          (_.sum(
            value?.map((item) =>
              amountType === 0 ? item?.currentAmount : item?.investedAmount,
            ),
          ) /
            _.sum(
              allHoldingsInsightsData?.map((item) =>
                amountType === 0 ? item?.currentAmount : item?.investedAmount,
              ),
            )) *
          100,
        totalCurrentValue: _.sum(
          allHoldingsInsightsData?.map((item) =>
            amountType === 0 ? item?.currentAmount : item?.investedAmount,
          ),
        ),
      };
    });

    return allCapSum
      ?.filter((dataItem) => dataItem?.cap === capName)[0]
      ?.totalCapPercentage?.toFixed(2);
  };

  // ========================================================================================================================================================================

  const totalValue = _.sum(
    allHoldingsInsightsData?.map((data: any) =>
      currentValueTabs === 0 ? data?.currentAmount : data?.investedAmount,
    ),
  );

  const capData = (capSize: string, sector?: string) =>
    allHoldingsInsightsData
      ?.filter((data: any) => {
        if (sector)
          return data.asset.sector === sector && data.asset.cap === capSize;
        return data.asset.cap === capSize;
      })
      .filter(
        (item: any, index: any, self: any) =>
          index ===
          self.findIndex((t: any) => t.asset.name === item.asset.name),
      )
      .map((element: any) => {
        const data = {
          name: element.asset.name,
          color: `#${(0x1000000 + Math.random() * 0xffffff)
            .toString(14)
            .substr(1, 6)}55`,
          loc:
            currentValueTabs === 0
              ? (element.currentAmount / totalValue) * 100
              : (element.investedAmount / totalValue) * 100,
          sector: element.asset.sector,
          capSize,
        };
        return data;
      })
      ?.filter((item: any) => item.loc > 0);

  useMemo(() => {
    if (pieClickData) {
      setSunBurstData({
        name: "sunburst",
        children: [
          {
            name: "Large Cap",
            color: LARGE_CAP_COLOR,
            children: capData("large")
              .sort((a: any, b: any) => b.loc - a.loc)
              .filter((item: any) => item.sector === pieClickData?.id),
          },
          {
            name: "Mid Cap",
            color: MID_CAP_COLOR,
            children: capData("medium")
              .sort((a: any, b: any) => b.loc - a.loc)
              .filter((item: any) => item.sector === pieClickData?.id),
          },
          {
            name: "Small Cap",
            color: SMALL_CAP_COLOR,
            children: capData("small")
              .sort((a: any, b: any) => b.loc - a.loc)
              .filter((item: any) => item.sector === pieClickData?.id),
          },
        ],
      });
      setCapSizes([
        {
          name: "Large Cap",
          value: handleNewFn("large", currentValueTabs),
          color: LARGE_CAP_COLOR,
        },
        {
          name: "Mid Cap",
          value: handleNewFn("medium", currentValueTabs),
          color: MID_CAP_COLOR,
        },
        {
          name: "Small Cap",
          value: handleNewFn("small", currentValueTabs),
          color: SMALL_CAP_COLOR,
        },
      ]);
    }
  }, [allHoldingsInsightsData, pieClickData]);

  const defaultData = () => {
    setSunBurstData({
      name: "GridKey Sunburst",
      children: [
        {
          name: "Large Cap",
          color: LARGE_CAP_COLOR,
          children: capData("large")
            .sort((a: any, b: any) => b.loc - a.loc)
            .map((dataItem, index) => {
              return {
                ...dataItem,
                color: PieChartColors[index < 12 ? index : index % 12],
              };
            }),
        },
        {
          name: "Mid Cap",
          color: MID_CAP_COLOR,
          children: capData("medium")
            .sort((a: any, b: any) => b.loc - a.loc)
            .map((dataItem, index) => {
              return {
                ...dataItem,
                color: PieChartColors[index < 12 ? index : index % 12],
              };
            }),
        },
        {
          name: "Small Cap",
          color: SMALL_CAP_COLOR,
          children: capData("small")
            .sort((a: any, b: any) => b.loc - a.loc)
            .map((dataItem, index) => {
              return {
                ...dataItem,
                color: PieChartColors[index < 12 ? index : index % 12],
              };
            }),
        },
      ],
    });
    // cap sizes
    setCapSizes([
      {
        name: "Large Cap",
        value: handleNewFn("large", currentValueTabs),
        color: LARGE_CAP_COLOR,
      },
      {
        name: "Mid Cap",
        value: handleNewFn("medium", currentValueTabs),
        color: MID_CAP_COLOR,
      },
      {
        name: "Small Cap",
        value: handleNewFn("small", currentValueTabs),
        color: SMALL_CAP_COLOR,
      },
    ]);
  };

  useMemo(() => {
    // pie data set
    setPieData(
      cumulativeSectorsWithPercentage()
        ?.map((item: any) => {
          const { sector } = item;
          const data = {
            id: sector,
            value: +item.companyPercentage,
            color: "",
          };

          return data;
        })
        ?.sort((a: any, b: any) => b.value - a.value)
        ?.map((item: any, index: number) => {
          return {
            ...item,
            color: PieChartColors[index < 12 ? index : index % 12],
          };
        }),
    );
    defaultData();
  }, [allHoldingsInsightsData, currentValueTabs === 0]);

  useEffect(() => {
    if (sunBurstRef.current)
      sunBurstRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
  }, [sunBurstRef?.current]);

  useEffect(() => {
    if (pieRef.current)
      pieRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
  }, [pieRef?.current]);

  return (
    <>
      {!isLoading && allHoldingsInsightsData?.length !== 0 && (
        <Card sx={{ mb: 1, padding: 1 }}>
          <Grid2 container>
            <Grid2
              size={3}
              display={"flex"}
              justifyContent={"flex-start"}
              alignItems={"center"}
              pt={3}
              pl={2}
            >
              <Typography
                fontWeight={700}
                fontSize={20}
                color={"var(--primary-900)"}
              >
                Asset Allocation{" "}
              </Typography>
            </Grid2>
            <Grid2
              size={9}
              display={"flex"}
              justifyContent={"flex-end"}
              alignItems={"center"}
              pt={3}
              pr={2}
            >
              <IconButton
                onClick={() => {
                  setPieClickData("");
                  setSunBurstClickData("");
                  setSunBurstHoverData("");
                  setPieHoverData("");
                  setCurrentValueTabs(0);
                  defaultData();
                }}
              >
                <ReloadIcon style={{ height: 23, width: 23 }} />
              </IconButton>
              <Divider
                orientation="vertical"
                style={{ height: "40px", margin: "0 10px" }}
              />
              <Tabs
                className="gridkey"
                onChange={(e, val) => {
                  setCurrentValueTabs(val);
                }}
                value={currentValueTabs}
                variant="standard"
              >
                <Tab disableRipple label="Current " />
                <Tab disableRipple label="Invested" />
              </Tabs>
            </Grid2>
            <Grid2 size={12} py={5} container>
              <Grid2 size={6}>
                <PieChartSection
                  pieData={pieData?.filter((data: any) => data?.value !== 0)}
                  pieClickData={pieClickData}
                  pieHoverData={pieHoverData}
                  setPieClickData={setPieClickData}
                  setPieHoverData={setPieHoverData}
                  pieRef={pieRef}
                />
              </Grid2>
              <Grid2 size={6}>
                <SunburstSection
                  sunBurstClickData={sunBurstClickData}
                  sunBurstHoverData={sunBurstHoverData}
                  setSunBurstClickData={setSunBurstClickData}
                  setSunBurstHoverData={setSunBurstHoverData}
                  sunBurstData={sunBurstData}
                  sunBurstRef={sunBurstRef}
                  capSizes={capSizes}
                  pieClickData={pieClickData}
                />
              </Grid2>
            </Grid2>
          </Grid2>
        </Card>
      )}

      <GKDataGrid
        density="compact"
        loading={holdingLoading}
        columns={assetsHoldingsColumns}
        rows={holdingData?.data?.data || []}
        zeroHolding
        totalCount={holdingData?.data?.totalCount || 0}
        name="insights"
        tableName="insights_list"
        onRowClick={onRowClick}
        pinnedColumns={{
          left: ["name"],
        }}
        exportFile={{
          path: "/holdings/selected/",
          exportName: "HoldingSummaryList",
        }}
      />
    </>
  );
};

export default InsigntsPageContent;
